import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Field, Th, Td, Money, NativeSelect } from "@/components/kit";
import { SIDE_TYPES } from "@/pages/Documents";
import { computeTotals, todayIso, errMsg } from "@/lib/format";

export const EINV = ["invoice", "deposit", "fee_note", "credit_note", "debit_note", "self_billing"];
const STAMP = ["invoice", "deposit", "fee_note", "debit_note", "self_billing"];
const STAMP_IN = ["supplier_invoice", "expense"];
const blank = (rate) => ({ item_id: "", description: "", qty: 1, unit_price: 0, discount_pct: 0, vat_rate: rate ?? 19, unit: "U" });

const LineRow = ({ l, i, items, rates, upd, remove, lang }) => (
  <tr className="border-b border-slate-50 align-top" data-testid={`line-${i}`}>
    <Td className="min-w-[220px]">
      <NativeSelect className="mb-1 w-full" value={l.item_id || ""} data-testid={`line-item-${i}`} onChange={(e) => {
        const it = items.find((x) => x.id === e.target.value);
        upd(i, it ? { item_id: it.id, code: it.code, description: it.name_fr, description_ar: it.name_ar, unit_price: it.price_ht, vat_rate: it.vat_rate, unit: it.unit } : { item_id: "" });
      }}>
        <option value="">— article libre —</option>
        {items.map((it) => <option key={it.id} value={it.id}>{`${it.code} · ${lang === "ar" && it.name_ar ? it.name_ar : it.name_fr}`}</option>)}
      </NativeSelect>
      <Input value={l.description} onChange={(e) => upd(i, { description: e.target.value })} data-testid={`line-desc-${i}`} />
    </Td>
    <Td><Input type="number" step="0.001" className="w-20" value={l.qty} onChange={(e) => upd(i, { qty: e.target.value })} data-testid={`line-qty-${i}`} /></Td>
    <Td><Input type="number" step="0.001" className="w-28" value={l.unit_price} onChange={(e) => upd(i, { unit_price: e.target.value })} data-testid={`line-price-${i}`} /></Td>
    <Td><Input type="number" step="0.01" className="w-20" value={l.discount_pct} onChange={(e) => upd(i, { discount_pct: e.target.value })} data-testid={`line-discount-${i}`} /></Td>
    <Td><NativeSelect value={l.vat_rate} onChange={(e) => upd(i, { vat_rate: +e.target.value })} data-testid={`line-vat-${i}`}>{rates.map((r) => <option key={r} value={r}>{`${r} %`}</option>)}</NativeSelect></Td>
    <Td className="text-end pt-4"><Money v={l.total_ht} /></Td>
    <Td><Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button></Td>
  </tr>
);

const ExpenseFields = ({ f, setF, accounts, t }) => (
  <Panel testid="editor-expense-panel">
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t("expense_account")}>
        <NativeSelect value={f.expense_account || ""} onChange={(e) => setF({ ...f, expense_account: e.target.value || null })} data-testid="editor-expense-account">
          <option value="">— 6061 —</option>
          {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.label}</option>)}
        </NativeSelect>
        {f.extraction?.expense_nature && <span className="mt-1 block text-[11px] text-amber-700" data-testid="editor-expense-ai-hint">{t("expense_ai_hint")}</span>}
      </Field>
      <Field label={t("paid_method")}>
        <NativeSelect value={f.paid_method || ""} onChange={(e) => setF({ ...f, paid_method: e.target.value || null })} data-testid="editor-paid-method">
          <option value="">{t("not_paid")}</option>
          {["cash", "card", "transfer", "cheque"].map((m) => <option key={m} value={m}>{t(`pm_${m}`)}</option>)}
        </NativeSelect>
      </Field>
    </div>
  </Panel>
);

export default function DocumentEditor() {
  const { api, t, lang, download } = useApp();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [fiscal, setFiscal] = useState({ vat_rates: [19, 13, 7, 0], stamp: 1 });
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) api.get(`/documents/${id}`).then((r) => setF(r.data.lines.length ? r.data : { ...r.data, lines: [blank()] }));
    else setF({ type: sp.get("type") || "invoice", date: todayIso(), lines: [blank()], global_discount_pct: 0, apply_stamp: true, currency: "TND", exchange_rate: 1, notes: "", stamp: 0 });
  }, [api, id, sp]);
  const side = f && (SIDE_TYPES.sale.includes(f.type) ? "sale" : "purchase");
  useEffect(() => {
    if (!side) return;
    api.get("/partners", { params: { kind: side === "sale" ? "client" : "supplier" } }).then((r) => setPartners(r.data));
    api.get("/items").then((r) => setItems(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data));
  }, [api, side]);
  useEffect(() => { if (f?.type === "expense") api.get("/expense-accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([])); }, [api, f?.type]);
  useEffect(() => { if (f?.date) api.get("/fiscal/current", { params: { date: f.date } }).then((r) => setFiscal(r.data)); }, [api, f?.date]);

  const stamp = !f ? 0 : STAMP.includes(f.type) ? (f.apply_stamp ? fiscal.stamp : 0) : STAMP_IN.includes(f.type) ? +f.stamp || 0 : f.source_id ? f.totals?.stamp || 0 : 0;
  const calc = useMemo(() => (f ? computeTotals(f.lines, +f.global_discount_pct || 0, stamp) : null), [f, stamp]);
  if (!f) return <Loading />;
  const upd = (i, patch) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const remove = (i) => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) });

  const save = async (validate) => {
    setBusy(true);
    setErrors([]);
    const body = { ...f, lines: calc.lines, global_discount_pct: +f.global_discount_pct || 0, exchange_rate: +f.exchange_rate || 1, stamp: +f.stamp || 0, partner_id: f.partner_id || null, warehouse_id: f.warehouse_id || null };
    try {
      const { data } = id ? await api.put(`/documents/${id}`, body) : await api.post("/documents", body);
      if (validate) {
        try { await api.post(`/documents/${data.id}/validate`); }
        catch (e) {
          const d = e.response?.data?.detail;
          if (d?.errors) setErrors(d.errors); else toast.error(errMsg(e));
          if (!id) nav(`/app/documents/${data.id}/edit`, { replace: true });
          return;
        }
      }
      nav(`/app/documents/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isE = EINV.includes(f.type);
  const T = calc.totals;
  return (
    <>
      <PageHeader eyebrow={f.number || t("st_draft")} title={t(`type_${f.type}`)}>
        <Button variant="outline" onClick={() => nav(-1)} data-testid="editor-cancel">{t("cancel")}</Button>
        <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="editor-save-draft">{t("save_draft")}</Button>
        <Button disabled={busy} onClick={() => save(true)} data-testid="editor-validate">{isE ? t("validate_send") : t("validate")}</Button>
      </PageHeader>
      {f.attachment_file_id && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900" data-testid="editor-attachment-banner">
          <Paperclip className="h-4 w-4" />{t("justif_attached")} : <b>{f.attachment_name}</b>
          <Button size="sm" variant="outline" className="ms-auto" onClick={() => download(`/files/${f.attachment_file_id}`, f.attachment_name)} data-testid="editor-attachment-open">{t("justif_file")}</Button>
        </div>
      )}
      {f.extraction && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="editor-ocr-banner">
          <span>{t("ocr_banner")}</span>
          {f.extraction.total_ttc != null && <span className="ms-auto">{t("ocr_read_total")} : <Money v={f.extraction.total_ttc} /> {Math.abs(f.extraction.total_ttc - T.ttc) > 0.01 && <b className="text-red-600">≠ <Money v={T.ttc} /></b>}</span>}
        </div>
      )}
      {errors.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="editor-control-errors">
          <div className="mb-2 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />{t("controls_failed")}</div>
          <ul className="list-disc space-y-1 ps-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      <Panel>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("partner")} className="lg:col-span-2">
            <NativeSelect value={f.partner_id || ""} onChange={set("partner_id")} data-testid="editor-partner">
              <option value="">—</option>{partners.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("date")}><Input type="date" value={f.date} onChange={set("date")} data-testid="editor-date" /></Field>
          <Field label={t("due_date")}><Input type="date" value={f.due_date || ""} onChange={set("due_date")} data-testid="editor-due-date" /></Field>
          <Field label={t("currency")}><NativeSelect value={f.currency} onChange={set("currency")} data-testid="editor-currency">{["TND", "EUR", "USD", "LYD", "DZD"].map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
          {f.currency !== "TND" && <Field label={t("exchange_rate")}><Input type="number" step="0.0001" value={f.exchange_rate} onChange={set("exchange_rate")} data-testid="editor-rate" /></Field>}
          <Field label={t("warehouse")}><NativeSelect value={f.warehouse_id || ""} onChange={set("warehouse_id")} data-testid="editor-warehouse"><option value="">—</option>{warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</NativeSelect></Field>
          {side === "purchase" && <Field label={t("supplier_ref")}><Input value={f.supplier_ref || ""} onChange={set("supplier_ref")} data-testid="editor-supplier-ref" /></Field>}
        </div>
      </Panel>
      {f.type === "expense" && <ExpenseFields f={f} setF={setF} accounts={accounts} t={t} />}
      <Panel title={t("lines")} action={<Button size="sm" variant="outline" onClick={() => setF({ ...f, lines: [...f.lines, blank(fiscal.vat_rates[0])] })} data-testid="editor-add-line"><Plus className="h-4 w-4 me-1" />{t("add_line")}</Button>}>
        <div className="overflow-x-auto -mx-4 sm:-mx-5">
          <table className="w-full min-w-[820px]">
            <thead className="border-b border-slate-100"><tr><Th>{t("description")}</Th><Th>{t("qty")}</Th><Th>{t("unit_price")}</Th><Th>{t("discount")}</Th><Th>{t("vat_rate")}</Th><Th className="text-end">{t("total_ht")}</Th><Th /></tr></thead>
            <tbody>{calc.lines.map((l, i) => <LineRow key={i} l={l} i={i} items={items} rates={fiscal.vat_rates} upd={upd} remove={remove} lang={lang} />)}</tbody>
          </table>
        </div>
      </Panel>
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("global_discount")}><Input type="number" step="0.01" value={f.global_discount_pct} onChange={set("global_discount_pct")} data-testid="editor-global-discount" /></Field>
            {STAMP.includes(f.type) && <label className="flex items-center gap-2 self-end pb-2 text-sm"><Switch checked={!!f.apply_stamp} onCheckedChange={(v) => setF({ ...f, apply_stamp: v })} data-testid="editor-apply-stamp" />{t("apply_stamp")}</label>}
            {STAMP_IN.includes(f.type) && <Field label={t("stamp")}><Input type="number" step="0.001" value={f.stamp || 0} onChange={set("stamp")} data-testid="editor-stamp" /></Field>}
            {f.type === "invoice" && (
              <div className="flex items-end gap-3 sm:col-span-2">
                <label className="flex items-center gap-2 pb-2 text-sm"><Switch checked={!!f.recurring?.active} onCheckedChange={(v) => setF({ ...f, recurring: { frequency: f.recurring?.frequency || "monthly", active: v } })} data-testid="editor-recurring" />{t("recurring")}</label>
                {f.recurring?.active && <NativeSelect value={f.recurring.frequency} onChange={(e) => setF({ ...f, recurring: { ...f.recurring, frequency: e.target.value } })} data-testid="editor-recurring-frequency">{["monthly", "quarterly", "yearly"].map((x) => <option key={x} value={x}>{t(x)}</option>)}</NativeSelect>}
              </div>
            )}
            <Field label={t("notes")} className="sm:col-span-2"><Textarea value={f.notes || ""} onChange={set("notes")} data-testid="editor-notes" /></Field>
          </div>
        </Panel>
        <Panel testid="editor-totals">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt>{t("total_ht")} (lignes)</dt><dd><Money v={T.lines_ht} cur={f.currency} /></dd></div>
            {T.global_discount > 0 && <div className="flex justify-between text-slate-500"><dt>{t("global_discount")}</dt><dd>-<Money v={T.global_discount} cur={f.currency} /></dd></div>}
            <div className="flex justify-between font-semibold"><dt>{t("total_ht")}</dt><dd data-testid="editor-total-ht"><Money v={T.ht} cur={f.currency} /></dd></div>
            {T.vat_breakdown.map((v) => <div key={v.rate} className="flex justify-between text-slate-600"><dt>TVA {v.rate} % <span className="text-xs">(<Money v={v.base} />)</span></dt><dd><Money v={v.amount} cur={f.currency} /></dd></div>)}
            {T.stamp > 0 && <div className="flex justify-between text-slate-600"><dt>{t("stamp")}</dt><dd><Money v={T.stamp} cur={f.currency} /></dd></div>}
            <div className="flex justify-between border-t border-slate-200 pt-3 text-lg font-bold text-teal-700"><dt>{t("total_ttc")}</dt><dd data-testid="editor-total-ttc"><Money v={T.ttc} cur={f.currency} /></dd></div>
          </dl>
        </Panel>
      </div>
    </>
  );
}
